import React, { useState } from 'react';
import styled from 'styled-components';
import { FiChevronLeft, FiChevronRight, FiX, FiZoomIn } from 'react-icons/fi';
import theme from '../../styles/theme';

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: ${props => props.$isModal ? theme.borderRadius.xl : theme.borderRadius.lg};
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  transition: transform 0.3s ease;

  &:hover {
    transform: ${props => props.$clickable ? 'scale(1.02)' : 'none'};
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
`;

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  font-size: ${theme.fontSizes.xl};
  cursor: pointer;
  padding: ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  transition: all 0.2s ease;
  z-index: 2;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &.prev {
    left: ${theme.spacing[3]};
  }

  &.next {
    right: ${theme.spacing[3]};
  }
`;

const Indicators = styled.div`
  position: absolute;
  bottom: ${theme.spacing[3]};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: ${theme.spacing[2]};
  z-index: 2;
`;

const Indicator = styled.button`
  width: 8px;
  height: 8px;
  border-radius: ${theme.borderRadius.full};
  border: none;
  background: ${props => props.$active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: white;
  }
`;

const ZoomButton = styled.button`
  position: absolute;
  top: ${theme.spacing[3]};
  left: ${theme.spacing[3]};
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
  padding: ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  transition: all 0.2s ease;
  z-index: 2;
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.9);
  z-index: ${theme.zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[6]};
`;

const ModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: transparent;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -${theme.spacing[8]};
  right: 0;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-size: ${theme.fontSizes.xl};
  cursor: pointer;
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  transition: all 0.2s ease;
  z-index: 3;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ImageCounter = styled.div`
  position: absolute;
  bottom: -${theme.spacing[8]};
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: ${theme.fontSizes.sm};
  background: rgba(0, 0, 0, 0.5);
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.full};
`;

const ImageCarousel = ({ images, isModal = false, onClose, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) {
    return null;
  }

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToImage = (index) => {
    setCurrentIndex(index);
  };

  const handleImageClick = () => {
    if (onImageClick && !isModal) {
      onImageClick(images);
    }
  };

  const currentImage = images[currentIndex];

  const carouselContent = (
    <CarouselContainer $isModal={isModal}>
      <ImageWrapper $clickable={!isModal && onImageClick} onClick={handleImageClick}>
        <Image src={currentImage.link} alt={`Imagem ${currentIndex + 1}`} />
        {!isModal && onImageClick && (
          <ZoomButton $show={images.length > 0} onClick={handleImageClick}>
            <FiZoomIn />
          </ZoomButton>
        )}
      </ImageWrapper>

      {images.length > 1 && (
        <>
          <NavigationButton
            className="prev"
            onClick={prevImage}
            disabled={images.length <= 1}
          >
            <FiChevronLeft />
          </NavigationButton>

          <NavigationButton
            className="next"
            onClick={nextImage}
            disabled={images.length <= 1}
          >
            <FiChevronRight />
          </NavigationButton>

          <Indicators>
            {images.map((_, index) => (
              <Indicator
                key={index}
                $active={index === currentIndex}
                onClick={() => goToImage(index)}
              />
            ))}
          </Indicators>
        </>
      )}
    </CarouselContainer>
  );

  if (isModal) {
    return (
      <ModalOverlay onClick={onClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <CloseButton onClick={onClose}>
            <FiX />
          </CloseButton>
          {carouselContent}
          {images.length > 1 && (
            <ImageCounter>
              {currentIndex + 1} / {images.length}
            </ImageCounter>
          )}
        </ModalContent>
      </ModalOverlay>
    );
  }

  return carouselContent;
};

export default ImageCarousel;
